import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft, Mountain, Heart, Zap, ClipboardList, TreePine, CalendarDays, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { MensRetreatBookingModal } from '@/components/MensRetreatBookingModal';
import { SEO } from '@/components/SEO';
import { StickyBookingButton } from '@/components/StickyBookingButton';
import { PageNavigation } from '@/components/PageNavigation';
import mensRetreatDetailImage from '@/assets/sunset-hiking-adventure.jpg';
const MensRetreat = () => {
  const navigate = useNavigate();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const navigationSections = [{
    id: 'hero',
    label: 'Overview',
    icon: Mountain
  }, {
    id: 'quick-info',
    label: 'Details',
    icon: ClipboardList
  }, {
    id: 'why',
    label: 'Why Join',
    icon: TreePine
  }, {
    id: 'program',
    label: 'Program',
    icon: CalendarDays
  }, {
    id: 'benefits',
    label: 'Benefits',
    icon: Sparkles
  }];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleBooking = () => {
    setIsBookingModalOpen(true);
  };
  const handleGoBack = () => {
    navigate('/#experiences');
  };
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Wolfpack in the Mountains - Men's Retreat",
    "description": "Rediscover Brotherhood, Strength, and Your Wild Nature in a transformative weekend retreat for men in the Polish mountains.",
    "image": "https://mysticforest.club/assets/mens-retreat-campfire.jpg",
    "startDate": "2025-11-14",
    "endDate": "2025-11-16",
    "eventStatus": "https://schema.org/EventSoldOut",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "Beskid Wyspowy",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Beskid Wyspowy",
        "addressCountry": "PL"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/SoldOut",
      "url": "https://mysticforest.club/experiences/mens-retreat"
    }
  };
  const faqItems = [{
    question: "What should I bring to the retreat?",
    answer: "Bring comfortable hiking clothes, warm layers for evening campfires, personal items, and an open heart. We recommend leaving your phone behind or keeping it on airplane mode for the full offline experience."
  }, {
    question: "Is this retreat suitable for beginners?",
    answer: "Yes! The retreat is designed for all fitness levels. The hiking pace is moderate, and activities can be adapted to individual needs. What matters most is your willingness to connect and be present."
  }, {
    question: "What's included in the retreat?",
    answer: "The retreat includes accommodation in a mountain shelter, all meals cooked together by the fire, guided workshops and circles, breathwork sessions, and the full weekend program. Transportation to the starting point is not included."
  }, {
    question: "Will there be phone signal in the mountains?",
    answer: "Limited or no phone signal is part of the experience. We encourage a full digital detox for the weekend. Emergency contact information will be provided to participants' families before the retreat."
  }];
  return <>
      <SEO title="Wolfpack in the Mountains - Men's Retreat" description="Rediscover Brotherhood, Strength, and Your Wild Nature. A transformative weekend retreat for men in Beskid Wyspowy, Poland. Nov 14-16, 2025." canonicalUrl="https://mysticforest.club/experiences/mens-retreat" ogType="event" keywords={['mens retreat', 'brotherhood', 'wilderness retreat', 'offline retreat', 'mountain retreat Poland', 'mens circle', 'transformational retreat']} breadcrumbs={[{
      name: 'Experiences',
      url: '/#experiences'
    }, {
      name: "Men's Retreat",
      url: '/experiences/mens-retreat'
    }]} faq={faqItems} jsonLd={jsonLd} />
      
      <div className="min-h-screen bg-background">
      <PageNavigation sections={navigationSections} />
      <StickyBookingButton onBookNow={handleBooking} label="Join Waitlist" />
      <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button variant="ghost" onClick={handleGoBack} className="flex items-center gap-2" aria-label="Go back to experiences">
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section id="hero" className="relative py-32">
        <div className="absolute inset-0">
          <img src={mensRetreatDetailImage} alt="Group of hikers with backpacks walking into sunset through mountain forest during men's retreat in Polish mountains, embodying brotherhood and wild nature connection" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-4xl">
          </span>
            <h1 className="text-4xl md:text-6xl font-serif font-light">
              Wolfpack in the Mountains
            </h1>
          </div>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Mystic Forest Men's Retreat – Rediscover Brotherhood, Strength, and Your Wild Nature
          </p>
          <div className="flex items-center justify-center gap-6 mb-8 text-lg">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>November 14–16, 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span>Beskid Sądecki, Poland</span>
            </div>
          </div>
          <div className="inline-flex items-center gap-3 px-6 md:px-8 py-4 bg-muted/50 border border-white/30 rounded-md backdrop-blur-sm">
            <span className="text-base md:text-lg font-medium text-white">
              This retreat is sold out
            </span>
            <span className="text-2xl">🐺</span>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section id="quick-info" className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">November 14–16, 2025</p>
              <p className="text-sm text-muted-foreground">Weekend retreat</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Beskid Wyspowy</p>
              <p className="text-sm text-muted-foreground">Polish mountains</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Men seeking connection</p>
              <p className="text-sm text-muted-foreground">Brotherhood & authenticity</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">3 Days / 2 Nights</p>
              <p className="text-sm text-muted-foreground">Fully offline</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Why Section */}
          <div id="why" className="mb-16">
            <h2 className="text-3xl font-serif font-light mb-8 text-center">
              🌲 Why?
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground text-center">
              <p className="text-xl leading-relaxed">
                A lone wolf can survive a day, but it's the pack that gives strength, direction, and safety.
              </p>
              <p className="text-lg">
                In a world where men often carry their burdens alone, we forget what it means to walk side by side – without masks, without competition.
                This retreat is a return to a primal rhythm: walking, fire, and brotherhood.
              </p>
            </div>
          </div>

          {/* Weekend Program */}
          <div id="program" className="mb-16">
            <h2 className="text-3xl font-serif font-light mb-8 text-center">
              🗓️ Weekend Program
            </h2>
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Day 1 – Friday</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Gathering the pack and hiking together into the mountains</li>
                    <li>• Silent walk – listening to your breath and the steps of your brothers</li>
                    <li>• Opening circle by the fire in a mountain shelter</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Day 2 – Saturday</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Morning ritual of breathwork and movement</li>
                    <li>• Mountain hike – shared effort, shared silence, shared strength</li>
                    <li>• Workshop: "The Lone Wolf or the Pack?" – exploring masks, roles, and authentic connection</li>
                    <li>• Evening fire – cooking together, stories, laughter, silence, presence</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Day 3 – Sunday</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Closing circle – what do you carry back home?</li>
                    <li>• Descent from the mountains – returning with new clarity</li>
                    <li>• Integration: bringing the pack's energy into everyday life</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Benefits Grid */}
          <div id="benefits" className="grid md:grid-cols-3 gap-8 mb-16">
            <div>
              <h3 className="text-2xl font-serif font-light mb-6 flex items-center gap-2">
                ✨ Why Join?
              </h3>
              <ul className="space-y-4 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Brotherhood:</strong> rediscover the strength of being together
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mountain className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Simplicity:</strong> mountains, fire, and honest conversations – nothing more needed
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Offline:</strong> phones stay behind – you are fully present
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Transformation:</strong> you return lighter, clearer, stronger
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-serif font-light mb-6">
                📌 Who is it for?
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Men tired of carrying the weight of responsibility alone</li>
                <li>• Those who long for simplicity, silence, and the wild</li>
                <li>• Men seeking authentic connection and space to just be</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-serif font-light mb-6">
                🏕️ What you'll gain
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• <strong className="text-foreground">Reset:</strong> clarity of thought, calm mind</li>
                <li>• <strong className="text-foreground">Brotherhood:</strong> strength that only a pack can give</li>
                <li>• <strong className="text-foreground">Renewal:</strong> body in motion, heart in fire, mind in peace</li>
              </ul>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center bg-muted/50 rounded-2xl p-12">
            <h3 className="text-3xl font-serif font-light mb-6 text-foreground">
              👉 Step into the Pack
            </h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join Mystic Forest Men's Retreat and feel the strength of brotherhood in the mountains.
              Limited spaces available for this transformative weekend.
            </p>
            <div className="inline-flex items-center gap-3 px-6 md:px-12 py-4 bg-muted/50 border border-muted-foreground/20 rounded-md">
              <span className="text-base md:text-lg font-medium text-muted-foreground">
                This retreat is sold out
              </span>
              <span className="text-2xl">🐺</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <MensRetreatBookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
     </div>
     </>;
};
export default MensRetreat;